import {
  Action,
  AssistantType,
  PromptVariable,
  SettingParam,
  VariableTypes,
} from "@/types/node";
import {useState, useEffect} from "react";
import {extractBracketedSections, getAllActions} from "@/utils/index";
import {findPromptValues} from "@/utils/index";
import {Node as ReactFlowNode, useReactFlow} from "@xyflow/react";
import SlatePromptEditor from "@/components/settings/PromptEditor/SlatePromptEditor";
import {VariableParams} from "@/types/general";
import {CustomAction} from "@/api/services/customAction";

export const getVariableParams = (
  assistantType: AssistantType,
): VariableParams => {
  const isModelInbound = assistantType === AssistantType.INBOUND;

  const variableParams = {
    [VariableTypes.VARIABLES]: {
      label: "Variables",
      name: "Variables",
      icon: "{}",
      open: "{",
      close: "}",
      content: [] as PromptVariable[],
    },
    [VariableTypes.CUSTOM_ACTIONS]: {
      label: "Actions",
      name: "Custom Actions",
      icon: "#",
      open: "#",
      close: " ",
      content: [] as PromptVariable[],
    },
    [VariableTypes.RESULTS]: {
      label: "Results",
      name: "Action Results",
      icon: "<>",
      open: "<",
      close: ">",
      content: [] as PromptVariable[],
    },
  };

  // Only show variables for Outbound models
  return isModelInbound
    ? Object.fromEntries(
        Object.entries(variableParams).filter(
          ([key]) => key !== VariableTypes.VARIABLES,
        ),
      )
    : variableParams;
};

export const highlightVariables = (
  text: string,
  nodes: ReactFlowNode[],
  assistantType: AssistantType,
) => {
  if (!text) return text;

  const variableParams = getVariableParams(assistantType);

  // Build a dynamic regex pattern from variableParams
  const regexPattern = Object.values(variableParams)
    .map(({open, close}) =>
      close
        ? `\\${open}[^\\${open}\\${close}]*\\${close}`
        : `\\${open}[^\\${open}]*\\s`,
    )
    .join("|");

  const regex = new RegExp(regexPattern, "g");

  return text.replace(regex, match => {
    const cleanMatch = match.replace(/\u200B/g, ""); // Remove zero-width spaces

    // Find the corresponding variable type based on brackets
    const matchedType = Object.keys(variableParams).find(
      type =>
        cleanMatch.startsWith(variableParams[type].open) &&
        cleanMatch.endsWith(variableParams[type].close),
    );

    if (!matchedType) return match; // If no match found, return original text

    const {open, close} = variableParams[matchedType];
    const content =
      cleanMatch.slice(open.length, cleanMatch.length - close.length).trim() ||
      "&#8203;"; // Keep empty markers visible

    const searchParam = content.split(":")[0];
    const isValidVariable =
      getAllVariables(
        nodes,
        searchParam,
        matchedType as VariableTypes,
        assistantType,
      ).length > 0;

    return (
      `<mark spellcheck="false" class="inline-block border px-1 pt-0 pb-0.5 rounded-md bg-white hover:bg-slate-100 gap-1 pointer-events-none">` +
      `<i class="not-italic font-semibold mr-1 ${isValidVariable ? "text-primary-accent" : "text-orange-500"}">${open}</i>` +
      `<span>${content}</span>` +
      `<i class="not-italic font-semibold ml-1 ${isValidVariable ? "text-primary-accent" : "text-orange-500"}">${close}</i>` +
      `</mark>`
    );
  });
};

export const getAllVariables = (
  nodes: ReactFlowNode[],
  filter: string,
  type: VariableTypes,
  assistantType: AssistantType,
): PromptVariable[] => {
  const variableParams = getVariableParams(assistantType);
  const promptValues = findPromptValues(nodes);
  const allActions = getAllActions(nodes);

  const isOutboundAssistant = assistantType === AssistantType.OUTBOUND;
  const allVariables = isOutboundAssistant
    ? extractBracketedSections(promptValues, [
        variableParams[VariableTypes.VARIABLES].open,
        variableParams[VariableTypes.VARIABLES].close,
      ])
    : [];

  const cleanedSearchParam = cleanupSearchParam(filter);

  if (type === VariableTypes.VARIABLES && isOutboundAssistant) {
    const customVariables = allVariables
      .filter(variable => (variable as string).includes(cleanedSearchParam))
      .map(variable => (variable as string).slice(1, -1));

    return customVariables as PromptVariable[];
  }

  if (type === VariableTypes.CUSTOM_ACTIONS) {
    const customActions = allActions
      .filter(action => {
        if (action.type === "transfer" && action.phone_number) {
          const formattedPhoneNumber = action.phone_number
            .toLowerCase()
            .split(" ")
            .join("_");
          return formattedPhoneNumber.includes(cleanedSearchParam);
        }

        const formattedName = action.name.toLowerCase().split(" ").join("_");
        return formattedName.includes(cleanedSearchParam);
      })
      .map(action => {
        if (action.type === "transfer" && action.phone_number) {
          return {
            formatted_name: action.phone_number
              .toLowerCase()
              .split(" ")
              .join("_"),
          };
        }
        return {
          formatted_name: action.name.toLowerCase().split(" ").join("_"),
        };
      });

    return customActions as unknown as CustomAction[];
  }

  if (type === VariableTypes.RESULTS) {
    const customActionsWithResults = allActions
      .filter((action: CustomAction) => {
        const formattedName = action.name.toLowerCase().split(" ").join("_");

        return (
          formattedName.includes(cleanedSearchParam) &&
          action.variables?.length > 0
        );
      })
      .map((action: CustomAction) => {
        const variables = action.variables;
        if (!variables) return;

        return {
          formatted_name: action.name.toLowerCase().split(" ").join("_"),
          variables: variables,
          http_mode: action.http_method || "",
        };
      });

    return customActionsWithResults as unknown as CustomAction[];
  }

  return [];
};

export const cleanupSearchParam = (searchParam: string) => {
  return searchParam.split(":")[0];
};

export const SettingPrompt = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
  assistantType,
}: {
  param: SettingParam;
  handleChange: (param: SettingParam, value: string) => void;
  selectedNodeId: string;
  disabled: boolean;
  assistantType: AssistantType;
}) => {
  const {getNodes} = useReactFlow();
  const nodes = getNodes();
  const [value, setValue] = useState(param.value?.toString() || "");
  const variableParams = getVariableParams(assistantType);

  const getVariablesData = (
    searchParam: string,
    type: VariableTypes,
  ): PromptVariable[] => {
    return getAllVariables(nodes, searchParam, type, assistantType);
  };

  useEffect(() => {
    setValue(param.value?.toString() || "");
  }, [param, selectedNodeId]);

  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-label mb-2 flex items-center justify-between">
        {param.label}
      </label>

      <div className="relative">
        <SlatePromptEditor
          value={value}
          placeholder={param.placeholder}
          variableParams={variableParams}
          onChange={value => {
            setValue(value);
            handleChange(param, value);
          }}
          getVariablesData={getVariablesData}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
